import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild } from "@angular/core";

// Animated "global brand protection" globe shown on the new login page.
// Ported from the standalone canvas animation; image layers live in assets/img/globe-anim.
const ASSET = "assets/img/globe-anim/";
const A = {
  base: ASSET + "base.webp",
  ringB: ASSET + "ringB.webp",
  ringG: ASSET + "ringG.webp",
  globe: { src: ASSET + "globe.webp", x: 179, y: 224 },
  icons: [
    { n: "shield", x: 638, y: 245, R: 173, bx: 741, by: 309, BR: 49 },
    { n: "qr", x: 170, y: 542, R: 152, bx: 251, by: 601, BR: 49 },
    { n: "award", x: 1119, y: 545, R: 169, bx: 1213, by: 615, BR: 49 },
    { n: "gift", x: 351, y: 957, R: 173, bx: 453, by: 1022, BR: 50 },
    { n: "ticket", x: 961, y: 952, R: 172, bx: 1059, by: 1021, BR: 50 },
  ],
};

const W = 1292, H = 1218, T = 10000, TAU = Math.PI * 2;
const GX = 645, GY = 690, GR = 455, RCX = 645, RCY = 650;
const FB = [555.917, 2.789, -23.449, 20.519, -6.933, 3.277, 5.94, 12.012, 0.472, 1.383, 0.777, 4.586, -2.738];
const FG = [522.853, 17.912, 21.051, 3.219, 6.552, 3.277, 5.94, 12.012, 0.472, 1.383, 0.777, 4.586, -2.738];
const HUB = [629, 689];
const LINKS: [number[], number[], string, number][] = [
  [[629, 689], [463, 845], "g", 0], [[629, 689], [737, 792], "b", 0.18], [[629, 689], [888, 501], "g", 0.36],
  [[737, 792], [905, 887], "b", 0.7], [[629, 689], [543, 640], "b", 0.84],
];
const ICON_R = [140, 133, 134, 138, 138];

const fit = (c: number[], t: number) => {
  let r = c[0];
  for (let k = 1; 2 * k < c.length; k++) { r += c[2 * k - 1] * Math.cos(k * t) + c[2 * k] * Math.sin(k * t); }
  return r;
};
const smooth = (e: number) => e * e * (3 - 2 * e);
// Resolves once the image is downloaded *and* decoded, so the first draw doesn't stall the main thread.
// Resolves null if the image fails, and the frame skips that layer.
const load = (s: string) => new Promise<HTMLImageElement>((res) => {
  const i: any = new Image();
  i.decoding = "async";
  if (typeof i.decode === "function") {
    i.src = s;
    i.decode().then(() => res(i), () => res(null));
  } else {
    i.onload = () => res(i); i.onerror = () => res(null);
    i.src = s;
  }
});

interface OrbitPath { P: number[][]; L: number; at(s: number): number[]; }

function buildPath(c: number[]): OrbitPath {
  const M = 2400, P: number[][] = [];
  let L = 0, prev: number[] = null;
  for (let i = 0; i <= M; i++) {
    const th = -Math.PI + TAU * i / M, r = fit(c, th), x = RCX + r * Math.cos(th), y = RCY + r * Math.sin(th);
    if (prev) { L += Math.hypot(x - prev[0], y - prev[1]); }
    P.push([x, y, L, th]); prev = [x, y];
  }
  return {
    P, L,
    at(s: number) {
      s = ((s % 1) + 1) % 1; const d = s * L;
      let lo = 0, hi = P.length - 1;
      while (hi - lo > 1) { const m = (lo + hi) >> 1; if (P[m][2] < d) { lo = m; } else { hi = m; } }
      const a = P[lo], b = P[hi], f = (d - a[2]) / Math.max(1e-6, b[2] - a[2]);
      return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[3] + (b[3] - a[3]) * f];
    },
  };
}

@Component({
  selector: "app-globe-animation",
  template: `
    <canvas #cv width="1292" height="1218" role="img"
      aria-label="Animated globe centred on India, ringed by shield, QR code, certification badge, gift and discount ticket icons with green check marks"></canvas>
  `,
  styles: [`
    :host { position: relative; display: block; }
    canvas { display: block; width: 100%; height: auto; opacity: 0; transform: scale(.88); transition: opacity .9s ease, transform 1.1s cubic-bezier(.2,.8,.2,1); }
    canvas.ga-ready { opacity: 1; transform: none; }
  `],
})
export class GlobeAnimationComponent implements AfterViewInit, OnDestroy {
  @ViewChild("cv", { static: true }) cvRef: ElementRef<HTMLCanvasElement>;

  playing = true;

  private ctx: CanvasRenderingContext2D;
  private ox: CanvasRenderingContext2D;
  private oc: HTMLCanvasElement;
  private scale = 1;
  private IMG: { [k: string]: HTMLImageElement } = {};
  private iconImgs: { img: HTMLImageElement; bimg: HTMLImageElement }[] = [];
  private parts: any[] = [];
  private PB = buildPath(FB);
  private PG = buildPath(FG);
  private NODESETS: any[];
  private rafId = 0;
  private t0 = 0;
  private held = 0;
  private destroyed = false;
  private onResize = () => this.sizeCanvas();

  constructor(private zone: NgZone) {
    let seed = 7;
    const rnd = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
    for (let i = 0; i < 70; i++) {
      const a = rnd() * TAU, rr = Math.sqrt(rnd()) * GR * 0.92;
      this.parts.push({
        x: GX + Math.cos(a) * rr, y: GY + Math.sin(a) * rr, ax: 6 + rnd() * 14, ay: 4 + rnd() * 10,
        k1: 1 + Math.floor(rnd() * 2), k2: 1 + Math.floor(rnd() * 2), k3: 1 + Math.floor(rnd() * 3),
        p1: rnd(), p2: rnd(), p3: rnd(), s: 0.8 + rnd() * 1.8, g: rnd() < 0.28,
      });
    }
    this.NODESETS = [
      { path: this.PB, dir: 1, N: 6, off: 0.03, rmin: 8, rmax: 15, c: "b" },
      { path: this.PB, dir: 1, N: 9, off: 0.11, rmin: 4, rmax: 7.5, c: "b" },
      { path: this.PG, dir: -1, N: 6, off: 0.07, rmin: 8, rmax: 14, c: "g" },
      { path: this.PG, dir: -1, N: 8, off: 0.15, rmin: 4, rmax: 7, c: "g" },
    ];
  }

  async ngAfterViewInit() {
    const cv = this.cvRef.nativeElement;
    this.ctx = cv.getContext("2d");
    this.oc = document.createElement("canvas");
    this.oc.width = W; this.oc.height = H;
    this.ox = this.oc.getContext("2d");

    // Fetch every layer in parallel. Only the base + globe are needed to start;
    // rings and icons are drawn as soon as each one arrives.
    load(A.ringB).then((i) => this.IMG.ringB = i);
    load(A.ringG).then((i) => this.IMG.ringG = i);
    A.icons.forEach((c, k) => {
      const slot: any = this.iconImgs[k] = { img: null, bimg: null };
      load(ASSET + "icon-" + c.n + ".webp").then((i) => slot.img = i);
      load(ASSET + "badge-" + c.n + ".webp").then((i) => slot.bimg = i);
    });
    const [base, globe] = await Promise.all([load(A.base), load(A.globe.src)]);
    this.IMG.base = base; this.IMG.globe = globe;
    if (this.destroyed) { return; }

    this.sizeCanvas();
    window.addEventListener("resize", this.onResize);
    try { this.playing = !matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { }
    this.t0 = performance.now();

    // Run the render loop outside Angular so each frame doesn't trigger change detection.
    this.zone.runOutsideAngular(() => {
      const loop = (now: number) => {
        if (this.destroyed) { return; }
        // Skip drawing while hidden (e.g. display:none on small screens)
        if (this.playing && this.cvRef.nativeElement.offsetParent !== null) { this.frame(now - this.t0); }
        this.rafId = requestAnimationFrame(loop);
      };
      this.frame(0);
      cv.classList.add("ga-ready");
      this.rafId = requestAnimationFrame(loop);
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    cancelAnimationFrame(this.rafId);
    window.removeEventListener("resize", this.onResize);
  }

  private sizeCanvas() {
    const cv = this.cvRef.nativeElement;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const s = Math.max(0.25, Math.min(dpr * cv.clientWidth / W, 1.6));
    cv.width = Math.round(W * s); cv.height = Math.round(H * s);
    this.scale = cv.width / W;
    if (!this.playing) { this.frame(this.held); }
  }

  private sphere(x: number, y: number, r: number, c: string) {
    const ctx = this.ctx;
    const glow = ctx.createRadialGradient(x, y, r * 0.7, x, y, r * 2.4);
    glow.addColorStop(0, c === "b" ? "rgba(40,130,255,0.35)" : "rgba(70,200,60,0.35)"); glow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow; ctx.fillRect(x - r * 2.4, y - r * 2.4, r * 4.8, r * 4.8);
    const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.08, x, y, r);
    if (c === "b") { g.addColorStop(0, "#e6f4ff"); g.addColorStop(.28, "#5fb0ff"); g.addColorStop(.75, "#1d63e8"); g.addColorStop(1, "#0e3fb0"); }
    else { g.addColorStop(0, "#f2ffd9"); g.addColorStop(.28, "#9de25a"); g.addColorStop(.75, "#3fae22"); g.addColorStop(1, "#1f7413"); }
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fill();
  }

  private trace(path: OrbitPath) {
    const ctx = this.ctx, P = path.P;
    ctx.beginPath(); ctx.moveTo(P[0][0], P[0][1]);
    for (let i = 6; i < P.length; i += 6) { ctx.lineTo(P[i][0], P[i][1]); }
    ctx.closePath();
  }

  private ringFlow(mask: HTMLImageElement, ang: number, col: string, rev: boolean) {
    const ox: any = this.ox, ctx: any = this.ctx;
    if (!mask || typeof ox.createConicGradient !== "function") { return; } // not loaded yet / older browsers: skip the ring shimmer
    ox.setTransform(1, 0, 0, 1, 0, 0); ox.globalCompositeOperation = "source-over"; ox.clearRect(0, 0, W, H);
    ox.drawImage(mask, 0, 0); ox.globalCompositeOperation = "source-in";
    const cg = ox.createConicGradient(ang, RCX, RCY);
    const c0 = `rgba(${col},0)`, c1 = `rgba(${col},1)`, cw = "rgba(255,255,255,1)";
    if (!rev) { cg.addColorStop(0, c0); cg.addColorStop(.2, c1); cg.addColorStop(.235, cw); cg.addColorStop(.24, c0); cg.addColorStop(.5, c0); cg.addColorStop(.7, c1); cg.addColorStop(.735, cw); cg.addColorStop(.74, c0); cg.addColorStop(1, c0); }
    else { cg.addColorStop(0, c0); cg.addColorStop(.26, c0); cg.addColorStop(.265, cw); cg.addColorStop(.3, c1); cg.addColorStop(.5, c0); cg.addColorStop(.76, c0); cg.addColorStop(.765, cw); cg.addColorStop(.8, c1); cg.addColorStop(1, c0); }
    ox.fillStyle = cg; ox.fillRect(0, 0, W, H);
    ctx.save(); ctx.globalCompositeOperation = "lighter"; ctx.filter = "blur(5px)"; ctx.globalAlpha = 0.9; ctx.drawImage(this.oc, 0, 0);
    ctx.filter = "none"; ctx.globalAlpha = 1; ctx.drawImage(this.oc, 0, 0); ctx.restore();
  }

  private frame(t: number) {
    const ctx = this.ctx, IMG = this.IMG;
    const p = (t % T) / T;
    ctx.setTransform(this.scale, 0, 0, this.scale, 0, 0);
    ctx.clearRect(0, 0, W, H);
    // halo behind artwork
    const hp = 0.5 + 0.5 * Math.sin(TAU * 2 * p);
    let g = ctx.createRadialGradient(GX, GY, GR * 0.9, GX, GY, GR * 1.32);
    g.addColorStop(0, `rgba(40,150,255,${0.30 + 0.14 * hp})`); g.addColorStop(1, "rgba(40,150,255,0)");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    if (IMG.base) { ctx.drawImage(IMG.base, 0, 0); }
    // globe breathing
    const gs = 1 + 0.007 * (0.5 + 0.5 * Math.sin(TAU * p - Math.PI / 2));
    if (IMG.globe) { ctx.save(); ctx.translate(GX, GY); ctx.scale(gs, gs); ctx.drawImage(IMG.globe, A.globe.x - GX, A.globe.y - GY); ctx.restore(); }
    // globe effects
    ctx.save(); ctx.beginPath(); ctx.arc(GX, GY, GR * gs, 0, TAU); ctx.clip();
    ctx.globalCompositeOperation = "lighter";
    g = ctx.createRadialGradient(GX - 60, GY - 40, 20, GX, GY, GR);
    g.addColorStop(0, `rgba(60,170,255,${0.06 + 0.08 * hp})`); g.addColorStop(.7, `rgba(30,120,255,${0.03 + 0.04 * hp})`); g.addColorStop(1, `rgba(120,220,255,${0.10 + 0.08 * hp})`);
    ctx.fillStyle = g; ctx.fillRect(GX - GR, GY - GR, GR * 2, GR * 2);
    // rotating meridians
    for (let k = 0; k < 3; k++) {
      const ph = (p + k / 3) % 1, lam = Math.PI * (ph - 0.5), rx = GR * Math.abs(Math.sin(lam));
      const al = 0.16 * Math.pow(Math.cos(lam), 1.5);
      ctx.strokeStyle = `rgba(140,230,255,${al})`; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.ellipse(GX, GY, Math.max(rx, 0.5), GR, 0, lam < 0 ? Math.PI / 2 : -Math.PI / 2, lam < 0 ? Math.PI * 1.5 : Math.PI / 2); ctx.stroke();
    }
    // connection lines with travelling light
    for (const [a, b, c, off] of LINKS) {
      const col = c === "g" ? "90,255,170" : "140,225,255";
      const q = (p * 2 + off) % 1, la = 0.10 + 0.10 * Math.sin(Math.PI * q);
      ctx.strokeStyle = `rgba(${col},${la})`; ctx.lineWidth = 2.2; ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
      const e = smooth(q), hx = a[0] + (b[0] - a[0]) * e, hy = a[1] + (b[1] - a[1]) * e, tl = Math.max(0, e - 0.22);
      const tx = a[0] + (b[0] - a[0]) * tl, ty = a[1] + (b[1] - a[1]) * tl;
      const fade = Math.sin(Math.PI * q);
      const lg2 = ctx.createLinearGradient(tx, ty, hx, hy); lg2.addColorStop(0, `rgba(${col},0)`); lg2.addColorStop(1, `rgba(${col},${0.85 * fade})`);
      ctx.strokeStyle = lg2; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(hx, hy); ctx.stroke();
      const rg = ctx.createRadialGradient(hx, hy, 0, hx, hy, 14); rg.addColorStop(0, `rgba(255,255,255,${0.9 * fade})`); rg.addColorStop(.35, `rgba(${col},${0.5 * fade})`); rg.addColorStop(1, `rgba(${col},0)`);
      ctx.fillStyle = rg; ctx.fillRect(hx - 14, hy - 14, 28, 28);
    }
    // hub pulse
    const hq = (p * 2) % 1;
    ctx.strokeStyle = `rgba(120,255,200,${0.5 * (1 - hq)})`; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(HUB[0], HUB[1], 6 + hq * 46, 0, TAU); ctx.stroke();
    // particles
    for (const o of this.parts) {
      const x = o.x + o.ax * Math.sin(TAU * (p * o.k1 + o.p1)), y = o.y + o.ay * Math.cos(TAU * (p * o.k2 + o.p2));
      const tw = Math.pow(0.5 + 0.5 * Math.sin(TAU * (p * o.k3 + o.p3)), 3);
      const r = o.s * 4, col = o.g ? "110,255,180" : "170,235,255";
      const rg = ctx.createRadialGradient(x, y, 0, x, y, r); rg.addColorStop(0, `rgba(255,255,255,${0.85 * tw})`); rg.addColorStop(.3, `rgba(${col},${0.5 * tw})`); rg.addColorStop(1, `rgba(${col},0)`);
      ctx.fillStyle = rg; ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    // horizontal scanning light
    const sq = (p * 2) % 1, sx = GX - GR - 160 + (2 * GR + 320) * smooth(sq);
    const sa = Math.sin(Math.PI * sq);
    const lg = ctx.createLinearGradient(sx - 150, 0, sx + 40, 0);
    lg.addColorStop(0, "rgba(80,200,255,0)"); lg.addColorStop(.75, `rgba(110,220,255,${0.16 * sa})`); lg.addColorStop(.97, `rgba(235,252,255,${0.42 * sa})`); lg.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = lg; ctx.fillRect(sx - 150, GY - GR, 190, GR * 2);
    ctx.restore();
    // orbital rings: light flowing in opposite directions
    this.ringFlow(IMG.ringB, TAU * p, "150,225,255", false);
    this.ringFlow(IMG.ringG, -TAU * p, "130,255,170", true);
    // orbital tracks
    ctx.save(); ctx.lineWidth = 1.8;
    this.trace(this.PB); ctx.strokeStyle = "rgba(31,105,230,0.55)"; ctx.stroke();
    this.trace(this.PG); ctx.strokeStyle = "rgba(60,170,50,0.55)"; ctx.stroke();
    ctx.restore();
    // travelling nodes
    for (const S of this.NODESETS) {
      for (let k = 0; k < S.N; k++) {
        const [x, y, th] = S.path.at(S.off + (k + S.dir * p) / S.N);
        const depth = 0.5 + 0.5 * Math.sin(th);
        this.sphere(x, y, S.rmin + (S.rmax - S.rmin) * depth, S.c);
      }
    }
    // icon states
    const ST: any[] = A.icons.map((c, i) => ({ fl: 5 * Math.sin(TAU * (p + i / 5)), rot: (2.4 * Math.PI / 180) * Math.sin(TAU * (2 * p + i * 0.37)) }));
    // linkage: hub -> every icon
    A.icons.forEach((c, i) => {
      const ex = c.x, ey = c.y + ST[i].fl, sx2 = HUB[0], sy2 = HUB[1];
      const mx = (sx2 + ex) / 2, my = (sy2 + ey) / 2, dx = ex - sx2, dy = ey - sy2, len = Math.hypot(dx, dy);
      const bend = (i % 2 ? 1 : -1) * 0.14 * len, qx = mx - dy / len * bend, qy = my + dx / len * bend;
      const bez = (u: number) => [(1 - u) * (1 - u) * sx2 + 2 * (1 - u) * u * qx + u * u * ex, (1 - u) * (1 - u) * sy2 + 2 * (1 - u) * u * qy + u * u * ey];
      ctx.save(); ctx.globalCompositeOperation = "lighter";
      ctx.beginPath(); ctx.moveTo(sx2, sy2); ctx.quadraticCurveTo(qx, qy, ex, ey);
      ctx.strokeStyle = `rgba(60,190,255,${0.30 + 0.10 * hp})`; ctx.lineWidth = 12; ctx.stroke();
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = "rgba(225,250,255,0.95)"; ctx.lineWidth = 2.6; ctx.stroke();
      ctx.setLineDash([4, 12]); ctx.lineDashOffset = -p * 16 * 10; ctx.strokeStyle = "rgba(40,230,140,1)"; ctx.lineWidth = 3; ctx.stroke(); ctx.setLineDash([]);
      ctx.globalCompositeOperation = "lighter";
      const q = (p * 2 + i / 5) % 1, u = smooth(q), fade = Math.min(1, Math.sin(Math.PI * q) * 1.6);
      for (let j = 0; j < 10; j++) {
        const uu = Math.max(0, u - j * 0.018), [x, y] = bez(uu), r = 10 - j * 0.7, al = fade * (1 - j / 10);
        const rg = ctx.createRadialGradient(x, y, 0, x, y, r * 2.2); rg.addColorStop(0, `rgba(255,255,255,${0.9 * al})`); rg.addColorStop(.35, `rgba(80,255,170,${0.6 * al})`); rg.addColorStop(1, "rgba(80,255,170,0)");
        ctx.fillStyle = rg; ctx.fillRect(x - r * 2.2, y - r * 2.2, r * 4.4, r * 4.4);
      }
      ctx.restore();
      ST[i].hit = Math.max(0, 1 - Math.abs(q - 0.86) / 0.14);
    });
    // hub core
    ctx.save(); ctx.globalCompositeOperation = "lighter";
    const hg = ctx.createRadialGradient(HUB[0], HUB[1], 0, HUB[0], HUB[1], 26); hg.addColorStop(0, "rgba(255,255,255,0.95)"); hg.addColorStop(.3, `rgba(110,255,200,${0.6 + 0.3 * hp})`); hg.addColorStop(1, "rgba(110,255,200,0)");
    ctx.fillStyle = hg; ctx.fillRect(HUB[0] - 26, HUB[1] - 26, 52, 52); ctx.restore();
    // icons
    A.icons.forEach((c, i) => {
      const fl = ST[i].fl, rot = ST[i].rot, hit = ST[i].hit, im = this.iconImgs[i];
      if (!im || !im.img || !im.bimg) { return; } // still loading
      ctx.save(); ctx.translate(c.x, c.y + fl);
      if (hit > 0) {
        ctx.save(); ctx.globalCompositeOperation = "lighter"; const R0 = ICON_R[i];
        const rg = ctx.createRadialGradient(0, 0, R0 - 14, 0, 0, R0 + 30); rg.addColorStop(0, "rgba(80,255,180,0)"); rg.addColorStop(.45, `rgba(110,230,255,${0.55 * hit})`); rg.addColorStop(1, "rgba(80,255,180,0)");
        ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(0, 0, R0 + 30, 0, TAU); ctx.fill(); ctx.restore();
      }
      ctx.rotate(rot);
      ctx.drawImage(im.img, -c.R, -c.R);
      if (hit > 0) { ctx.save(); ctx.globalCompositeOperation = "lighter"; ctx.strokeStyle = `rgba(170,240,255,${0.7 * hit})`; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(0, 0, ICON_R[i] - 2, 0, TAU); ctx.stroke(); ctx.restore(); }
      const bx = c.bx - c.x, by = c.by - c.y, pu = 0.5 + 0.5 * Math.sin(TAU * (2 * p + i * 0.2));
      ctx.globalCompositeOperation = "lighter";
      const gr2 = c.BR + 20, rg2 = ctx.createRadialGradient(bx, by, c.BR * 0.8, bx, by, gr2);
      rg2.addColorStop(0, `rgba(40,220,120,${0.28 * pu})`); rg2.addColorStop(1, "rgba(40,220,120,0)");
      ctx.fillStyle = rg2; ctx.fillRect(bx - gr2, by - gr2, gr2 * 2, gr2 * 2);
      ctx.globalCompositeOperation = "source-over";
      const s = 1 + 0.045 * pu; ctx.translate(bx, by); ctx.rotate(-rot); ctx.scale(s, s); ctx.drawImage(im.bimg, -c.BR, -c.BR);
      ctx.restore();
    });
  }
}
